import {
	adminGetHome,
	backofficeAuthGetMe,
	backofficeAuthGetSession,
	geoListInstitutions,
	supportListOpenRequests
} from '@repo/sdk/gen'
import { type QueryClient, queryOptions } from '@tanstack/react-query'
import { makeQueryKeys } from '@/query/query-keys'

/**
 * Session freshness pact: the session query has staleTime Infinity, so route
 * guards read it cache-first and never await the network mid-session (the
 * router shows the pending page at 0ms). Every point where the auth state has
 * just changed server-side — the post-sign-in flows — revalidates through
 * this: fetchQuery + staleTime 0 refetches and awaits, while ensureQueryData
 * would return the stale cached value and loop the login. Logout and the 401
 * recovery revalidate by clearing the cache instead.
 */
export function fetchFreshSession(queryClient: QueryClient) {
	return queryClient.fetchQuery({
		...adminSessionQueryOptions(),
		staleTime: 0
	})
}

export function meQueryOptions() {
	return queryOptions({
		queryKey: makeQueryKeys.admins.me(),
		queryFn: async () => {
			const response = await backofficeAuthGetMe({ body: {} })
			return response.data
		}
	})
}

export function adminSessionQueryOptions() {
	return queryOptions({
		/** Keep the CSRF token available while the HttpOnly session cookie is valid. */
		gcTime: Number.POSITIVE_INFINITY,
		/** Refetched only via auth invalidation (login/logout) or the 401 cache clear, never per navigation. */
		staleTime: Number.POSITIVE_INFINITY,
		queryKey: makeQueryKeys.auth.session(),
		queryFn: async () => {
			const data = await backofficeAuthGetSession()

			if (data.sessionType !== 'authenticated') {
				return { sessionType: 'unauthenticated' as const }
			}

			if (!(data.session && data.admin && data.csrfToken)) {
				throw new Error('Authenticated session payload is incomplete')
			}

			return {
				...data,
				sessionType: 'authenticated' as const,
				csrfToken: data.csrfToken,
				session: data.session,
				admin: data.admin
			}
		}
	})
}

/**
 * The university catalogue the residence form and the call page read. It
 * changes only when the ministry data is imported again, so it is kept for the
 * session.
 */
export function institutionsQueryOptions() {
	return queryOptions({
		queryKey: makeQueryKeys.geo.institutions(),
		queryFn: async ({ signal }) => {
			const response = await geoListInstitutions({ signal })
			return response.data
		},
		staleTime: Number.POSITIVE_INFINITY
	})
}

/**
 * The first page of the general admin: the open anomalies, the calls and how many
 * requests for help are open. It holds no student, so it is read again when the
 * page opens and when the window comes back into focus: a failure that opened a
 * minute ago must not wait for the ten minutes of the defaults. The commands that
 * change what it shows invalidate `adminHome`.
 */
export function adminHomeQueryOptions() {
	return queryOptions({
		queryKey: makeQueryKeys.adminHome.all(),
		queryFn: async ({ signal }) => (await adminGetHome({ signal })).data,
		staleTime: 0,
		refetchOnWindowFocus: true
	})
}

/**
 * The oldest requests for help still open, with who asked. They name students,
 * so every fetch is a `personal_data.viewed` fact (decision D37): it keeps the
 * client defaults, one opening is one access. It is a child of `adminHome`, so
 * what invalidates the page reads it again after a command.
 */
export function openRequestsQueryOptions() {
	return queryOptions({
		queryKey: makeQueryKeys.adminHome.requests(),
		queryFn: async ({ signal }) =>
			(
				await supportListOpenRequests({
					body: { page: 1, limit: 5 },
					signal
				})
			).data
	})
}
